/**
 * The "New" line — the same capture for everyone. Type one line and press Enter:
 * "Call Joseph re Msida leak tomorrow 10am #IFS @marina !high". ONE shows what it understood before you save.
 * Pasting a long text (an email, notes) opens Smart add: Fusion AI proposes task lines, you tick and edit them.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, CalendarDays, CornerDownLeft, Flag, Lock, Plus, Repeat, Sparkles, Tag, User, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { post } from "../lib/api";
import { newClientRef, sendOrQueue } from "../lib/offline";
import { ErrorNote, Modal } from "./ui";
import { repeatLabel, type RepeatRule } from "../lib/repeat";
import { CategoryPill } from "./Categories";

interface Preview {
  parsed: { title: string; priority?: string; decisionNeeded?: boolean; date?: { plannedDate: string; dueAt: string; hasTime: boolean; text: string }; repeat?: RepeatRule };
  company: { code: string; name: string } | null;
  team: { name: string } | null;
  assignees: Array<{ id: string; name: string }>;
  category?: { id: string | null; name: string; color: string | null; isNew: boolean } | null;
  warnings: string[];
  destination: string;
}

const PRIORITY_LABEL: Record<string, string> = { P1: "Urgent", P2: "High", P3: "Normal", P4: "Low" };

/** Pasted text that is clearly more than one task line. */
const isLongPaste = (t: string) => t.includes("\n") || t.length > 160;

function when(d: NonNullable<Preview["parsed"]["date"]>) {
  const dt = new Date(d.dueAt);
  const day = dt.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
  return d.hasTime ? `${day}, ${dt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : day;
}

export function QuickAdd({ autoFocus, onCreated, placeholder, onLongPaste }: { autoFocus?: boolean; onCreated?: (item: { id: string; ref: string; title: string }) => void; placeholder?: string; onLongPaste?: (text: string) => void }) {
  const qc = useQueryClient();
  const [smart, setSmart] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    const t = text.trim();
    if (!t) return setPreview(null);
    const n = ++seq.current;
    const h = setTimeout(() => {
      void post<Preview>("/api/tasks/parse", { text: t }).then((p) => n === seq.current && setPreview(p)).catch(() => undefined);
    }, 250);
    return () => clearTimeout(h);
  }, [text]);

  const save = async () => {
    const line = text.trim();
    if (!line || busy) return;
    setBusy(true);
    setError(null);
    setText(""); // clear at once so the next thing typed starts fresh
    setPreview(null);
    try {
      const sent = await sendOrQueue<{ item: { id: string; ref: string; title: string }; warnings: string[] }>({ method: "POST", path: "/api/tasks/quick", body: { text: line, clientRef: newClientRef() }, label: `Add “${line.slice(0, 60)}”` });
      if (sent.queued) { setSaved("No signal — kept on this phone and added as soon as you're back online."); setTimeout(() => setSaved(null), 6000); return; }
      const r = sent.result;
      setSaved(`Added “${r.item.title}”${r.warnings.length ? ` · ${r.warnings.join(" · ")}` : ""}`);
      setTimeout(() => setSaved(null), 4000);
      void qc.invalidateQueries({ queryKey: ["my-day"] });
      void qc.invalidateQueries({ queryKey: ["items"] });
      onCreated?.(r.item);
    } catch (e) {
      setError(e);
      setText((cur) => cur || line); // give the words back if saving failed
    } finally {
      setBusy(false);
    }
  };

  const p = preview;
  const showChips = !!p && !!text.trim() && (p.parsed.date || p.parsed.repeat || p.category || p.parsed.priority || p.company || p.team || p.assignees.length || p.warnings.length || p.parsed.decisionNeeded);
  return (
    <div>
      <div className="flex items-center gap-2 rounded-xl border border-[var(--line)] bg-[var(--panel)] px-3 py-2 focus-within:border-brand-400">
        <Plus size={18} className="muted shrink-0" />
        <input
          className="min-w-0 flex-1 bg-transparent py-1 text-[0.9375rem] outline-none"
          placeholder={placeholder ?? "Add a task… e.g. Call Joseph tomorrow 10am #IFS @marina !high"}
          value={text}
          autoFocus={autoFocus}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void save(); } }}
          onPaste={(e) => {
            const t = e.clipboardData.getData("text");
            if (!isLongPaste(t)) return;
            e.preventDefault();
            if (onLongPaste) onLongPaste(t);
            else setSmart(t);
          }}
          aria-label="New task"
        />
        <button className="btn btn-primary !min-h-0 !px-2.5 !py-1 text-xs" disabled={!text.trim() || busy} onClick={() => void save()}>
          <CornerDownLeft size={13} /> Add
        </button>
      </div>
      {showChips && p && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
          <span className="muted">→ {p.parsed.title || "…"}</span>
          <Chips p={p} />
        </div>
      )}
      {saved && <p className="mt-2 text-xs text-emerald-700">{saved}</p>}
      <ErrorNote error={error} />
      {smart !== null && (
        <Modal title="Smart add" wide onClose={() => setSmart(null)}>
          <SmartAdd initial={smart} onDone={(n) => { setSmart(null); setSaved(`Added ${n} task${n === 1 ? "" : "s"}`); setTimeout(() => setSaved(null), 4000); }} />
        </Modal>
      )}
    </div>
  );
}

function Chips({ p }: { p: Preview }) {
  return (
    <>
      {p.parsed.date && <span className="chip"><CalendarDays size={12} /> {when(p.parsed.date)}</span>}
      {p.parsed.repeat && <span className="chip"><Repeat size={12} /> {repeatLabel(p.parsed.repeat)}</span>}
      {p.parsed.priority && <span className="chip"><Flag size={12} /> {PRIORITY_LABEL[p.parsed.priority]}</span>}
      {p.parsed.decisionNeeded && <span className="chip">Decision needed</span>}
      {p.category && (p.category.color ? <CategoryPill c={{ id: p.category.id!, name: p.category.name, color: p.category.color }} /> : <span className="chip border-dashed">/{p.category.name} · new</span>)}
      {p.company && <span className="chip"><Tag size={12} /> {p.company.code}</span>}
      {p.assignees.map((a) => <span key={a.id} className="chip"><User size={12} /> {a.name}</span>)}
      <span className="chip">{p.team ? <Users size={12} /> : <Lock size={12} />} {p.destination}</span>
      {p.warnings.map((w) => <span key={w} className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle size={12} /> {w}</span>)}
    </>
  );
}

type Proposed = Preview & { line: string; why: string | null; keep: boolean };

/** One proposed line: editable; ONE re-reads it as you type, exactly like the New line. */
function ProposedRow({ t, onChange }: { t: Proposed; onChange: (t: Proposed) => void }) {
  const seq = useRef(0);
  const edit = (line: string) => {
    onChange({ ...t, line });
    const n = ++seq.current;
    setTimeout(() => {
      if (n !== seq.current || !line.trim()) return;
      void post<Preview>("/api/tasks/parse", { text: line.trim() }).then((p) => n === seq.current && onChange({ ...t, ...p, line, keep: t.keep })).catch(() => undefined);
    }, 300);
  };
  return (
    <li className={`flex gap-2.5 py-2.5 ${t.keep ? "" : "opacity-50"}`}>
      <input type="checkbox" className="mt-2 size-4 shrink-0" checked={t.keep} onChange={(e) => onChange({ ...t, keep: e.target.checked })} aria-label="Keep this task" />
      <div className="min-w-0 flex-1">
        <input className="input !py-1.5 text-sm" value={t.line} onChange={(e) => edit(e.target.value)} />
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs"><Chips p={t} /></div>
        {t.why && <p className="muted mt-1 text-xs">From: {t.why}</p>}
      </div>
    </li>
  );
}

/** Smart add: paste text → Fusion AI proposes tasks → tick, edit, add. Nothing is created until you press Add. */
export function SmartAdd({ initial = "", onDone }: { initial?: string; onDone: (added: number) => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState(initial);
  const [tasks, setTasks] = useState<Proposed[] | null>(null);
  const [busy, setBusy] = useState<"split" | "add" | null>(null);
  const [error, setError] = useState<unknown>(null);

  const split = async () => {
    if (!text.trim() || busy) return;
    setBusy("split");
    setError(null);
    try {
      const r = await post<{ tasks: Array<Preview & { line: string; why: string | null }> }>("/api/tasks/split", { text: text.trim() });
      setTasks(r.tasks.map((t) => ({ ...t, keep: true })));
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };
  useEffect(() => {
    if (initial.trim()) void split();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = async () => {
    const keep = (tasks ?? []).filter((t) => t.keep && t.line.trim());
    if (!keep.length || busy) return;
    setBusy("add");
    setError(null);
    const source = text.trim().slice(0, 5000);
    let n = 0;
    try {
      for (const t of keep) {
        const description = `${t.why ? `From: ${t.why}\n\n` : ""}Added with Smart add from:\n${source}`;
        await post("/api/tasks/quick", { text: t.line.trim(), description });
        n++;
      }
      void qc.invalidateQueries({ queryKey: ["my-day"] });
      void qc.invalidateQueries({ queryKey: ["items"] });
      onDone(n);
    } catch (e) {
      setError(e);
      setTasks((cur) => (cur ?? []).map((t) => (keep.slice(0, n).includes(t) ? { ...t, keep: false } : t))); // don't add the saved ones twice
    } finally {
      setBusy(null);
    }
  };

  const count = (tasks ?? []).filter((t) => t.keep).length;
  return (
    <div className="space-y-3">
      {tasks === null ? (
        <>
          <textarea className="input min-h-40 text-sm" placeholder="Paste an email, meeting notes or a list. Fusion AI picks out the tasks and you confirm them." value={text} onChange={(e) => setText(e.target.value)} autoFocus={!initial} />
          <div className="flex items-center justify-between gap-2">
            <p className="muted text-xs">Nothing is added until you confirm.</p>
            <button className="btn btn-primary" disabled={!text.trim() || !!busy} onClick={() => void split()}>
              <Sparkles size={15} /> {busy === "split" ? "Reading…" : "Find tasks"}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="muted text-xs">Fusion AI found {tasks.length} task{tasks.length === 1 ? "" : "s"}. Untick what you don't want and edit any line — dates, #company, #team, @person and !high work as usual.</p>
          <ul className="divide-y divide-[var(--line)]">
            {tasks.map((t, i) => <ProposedRow key={i} t={t} onChange={(nt) => setTasks((cur) => (cur ?? []).map((x, j) => (j === i ? nt : x)))} />)}
          </ul>
          <div className="flex items-center justify-between gap-2 border-t border-[var(--line)] pt-3">
            <button className="btn" disabled={!!busy} onClick={() => setTasks(null)}>Back to text</button>
            <button className="btn btn-primary" disabled={!count || !!busy} onClick={() => void add()}>
              <Plus size={15} /> {busy === "add" ? "Adding…" : `Add ${count} task${count === 1 ? "" : "s"}`}
            </button>
          </div>
        </>
      )}
      {busy === "split" && <p className="muted text-xs">Fusion AI is reading the text…</p>}
      <ErrorNote error={error} />
    </div>
  );
}

/** Global "New": the one line, plus the way to ask a team (request forms) when it's not your own task. */
export function NewSheet({ onClose, onAskTeam }: { onClose: () => void; onAskTeam: () => void }) {
  const navigate = useNavigate();
  const [smart, setSmart] = useState<string | null>(null);
  if (smart !== null) {
    return (
      <Modal title="Smart add" wide onClose={onClose}>
        <SmartAdd initial={smart} onDone={() => onClose()} />
      </Modal>
    );
  }
  return (
    <Modal title="New" onClose={onClose}>
      <div className="space-y-4">
        <QuickAdd autoFocus onCreated={() => onClose()} onLongPaste={(t) => setSmart(t)} />
        <div className="muted space-y-1 text-xs">
          <p>Just type what needs doing. Optional: a date or time (“friday”, “tomorrow 10am”), <b>#company</b> or <b>#team</b>, <b>@person</b> to hand it over, <b>!high</b> / <b>!urgent</b>, <b>!decide</b>, <b>every friday</b> / <b>monthly</b> to repeat, <b>/category</b>, <b>~WRK-…</b> to link a task.</p>
          <p>It's private to you unless you hand it to someone or send it to a team.</p>
        </div>
        <div className="flex flex-wrap gap-2 border-t border-[var(--line)] pt-3">
          <button className="btn" onClick={() => setSmart("")}><Sparkles size={15} /> Paste an email or notes…</button>
          <button className="btn" onClick={onAskTeam}><Users size={15} /> Ask a team for something…</button>
          <button className="btn" onClick={() => { onClose(); void navigate({ to: "/work" }); }}>Open my tasks</button>
        </div>
      </div>
    </Modal>
  );
}
